/**
 * SizerWorkspace — the one page a scenario lives on, in two modes.
 *
 *   Review       the stored answer for the version on screen (KPI strip, the
 *                sizer's result panels, the inputs that priced it)
 *   Edit inputs  the sizer form + a sticky rail: the re-price action card, then
 *                the results (dimmed and tagged "Stale" once the form drifts)
 *
 * It replaces the old four-screen loop — sizer → results → Priced Scenarios →
 * drilldown → "Open in sizer" — with the list plus this page. The header
 * (ScenarioWorkspaceHeader) is pinned under the Sizers tab strip and carries
 * the name, the mode toggle, the version picker, the ⋯ menu and Save as deal;
 * the fixed footer bar is gone.
 *
 * The sizer keeps owning everything about pricing: its form, its payload, its
 * price / save calls and its result rendering. This component only arranges
 * them and works out what changed (`baseline` vs `values`).
 *
 * A BLANK sizer (no `scenarioId`) renders the same Edit layout with a plain
 * title; once it prices and the backend persists the run, the sizer moves the
 * URL to `?scenario=<id>&mode=edit` and the page becomes a workspace.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import Flx from "../../../components/layout/Flx";
import { SIZER_TABS_H } from "../../../AuthenticatedDashboard/sizersNav";
import {
  SZ,
  SIZER_GUTTER,
  SizerShell,
  Workbench,
  ResultsCol,
  scrollSizerToTop,
  ChangedFieldsProvider,
} from "../sizerUi";
import ScenarioWorkspaceHeader, {
  OlderVersionBanner,
} from "./ScenarioWorkspaceHeader";
import RailActions, {
  PILL_BOTTOM,
  RepricePill,
  StaleWrap,
  pillActive,
} from "./RepriceCard";
import ScenarioReview from "./ScenarioReview";
import { WORKSPACE_MODES, diffScenarioValues } from "./workspaceModel";
import { versionFigures } from "../scenarioModel";
import { WsSecondaryButton } from "./workspaceParts";

const neg = (g) => ({ xs: -g.xs, md: -g.md });

export default function SizerWorkspace({
  rootRef,
  // Blank sizer header (+ an optional switcher under it, e.g. DSCR's
  // Full / Quick entry tabs — blank sizers only)
  title,
  subtitle,
  headerTabs,
  // Persisted scenario
  scenarioId,
  doc,
  onDocChange,
  typeLabel,
  shownVersion,
  onLoadVersion,
  mode,
  onModeChange,
  onDuplicate,
  // Header slot: the sizer's "Admin tools" menu.
  adminTools,
  // Change tracking — `baseline` is what the numbers on screen were priced
  // from (the loaded version's inputs, or the last run this session).
  values,
  baseline,
  onDiscard,
  // Pricing
  result,
  pricePending,
  priceError,
  onPrice,
  blockedReason,
  liveNote,
  // Opens the sizer's explain panel from the re-price card (Inputs view).
  onExplain,
  // Save as deal
  onSave,
  savePending,
  // Content
  form,
  rail,
  railAfter,
  kpis,
  panels,
  inputCells,
  children,
}) {
  const hasScenario = Boolean(scenarioId);
  const isReview = hasScenario && mode === WORKSPACE_MODES.REVIEW;

  const changes = useMemo(
    () => (baseline ? diffScenarioValues(baseline, values) : []),
    [baseline, values],
  );
  const dirty = changes.length > 0;
  const [pillH, setPillH] = useState(0);
  const showPill = pillActive({
    hasScenario,
    hasResult: Boolean(result),
    changes,
  });
  // path → previous value, for the per-field "was …" markers.
  const changedMap = useMemo(
    () => new Map(changes.map((c) => [c.path, c.from])),
    [changes],
  );
  const nextVersion = (doc?.currentVersion || 0) + 1;

  const saveDisabledReason = blockedReason
    ? "Fill the required fields first"
    : (hasScenario || result) && dirty
      ? "Re-price first — the deal would carry stale pricing"
      : null;

  // The Results tab's "v3 · 8.500%" — the version on screen and its headline
  // figure (versionFigures: RTL/GUC required coupon, DSCR coupon).
  const headline = result
    ? versionFigures(doc?.scenarioType, result).primary
    : null;
  // Just the figure — the version is already on the picker beside it.
  const resultsSub = headline && headline !== "—" ? headline : null;

  const shownRun = (doc?.versions || []).find(
    (v) => v.version === shownVersion,
  );
  const shownScenario =
    shownRun?.scenario ||
    (shownVersion === doc?.currentVersion ? doc?.scenario : null);

  // The rail pins under BOTH sticky strips (tabs + this header), so it reads the
  // header's live height rather than guessing it.
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setHeaderH(el.offsetHeight));
    ro.observe(el);
    setHeaderH(el.offsetHeight);
    return () => ro.disconnect();
  }, [hasScenario]);

  // Back to the top on arrival and when the mode flips — the two layouts share
  // nothing below the header, so staying scrolled lands mid-way down an
  // unrelated card. Scrolls only the Sizers column (its nearest scroller):
  // `scrollIntoView` used to scroll the whole document too and tuck the top
  // of the page under the pinned tab strip.
  useEffect(() => {
    scrollSizerToTop(rootRef?.current, { instant: true });
  }, [isReview, rootRef]);

  const header = (
    <Box
      ref={headerRef}
      sx={{
        position: "sticky",
        top: SIZER_TABS_H,
        zIndex: 60,
        mx: neg(SIZER_GUTTER),
        px: SIZER_GUTTER,
        mb: 3,
        bgcolor: "rgba(246,248,249,.88)",
        backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${SZ.line}`,
      }}
    >
      <ScenarioWorkspaceHeader
        title={title}
        subtitle={subtitle}
        scenarioId={scenarioId}
        doc={doc}
        onDocChange={onDocChange}
        typeLabel={typeLabel}
        shownVersion={shownVersion}
        shownScenario={shownScenario}
        onLoadVersion={onLoadVersion}
        mode={isReview ? WORKSPACE_MODES.REVIEW : WORKSPACE_MODES.EDIT}
        onModeChange={onModeChange}
        changesCount={hasScenario || result ? changes.length : 0}
        resultsSub={resultsSub}
        adminTools={adminTools}
        onDuplicate={onDuplicate}
        onSave={onSave}
        savePending={savePending}
        saveDisabledReason={saveDisabledReason}
      />
    </Box>
  );

  const current = doc?.versions?.find((v) => v.version === doc?.currentVersion);

  return (
    // The rail pins below the tab strip and the header; its own scroll height
    // is worked out from the same number.
    <Box
      sx={{
        "--sizer-sticky-top": `${SIZER_TABS_H + headerH + 8}px`,
      }}
    >
      {/* Room under the last field for the floating pill while it is up. */}
      <SizerShell
        rootRef={rootRef}
        header={header}
        pb={
          !isReview && showPill && pillH
            ? `${pillH + PILL_BOTTOM + 12}px`
            : "12px"
        }
      >
        {!hasScenario && headerTabs && (
          <Box sx={{ mt: -1, mb: 2.75 }}>{headerTabs}</Box>
        )}
        {hasScenario && (
          <OlderVersionBanner
            doc={doc}
            shownVersion={shownVersion}
            onBackToCurrent={() => current && onLoadVersion?.(current)}
            onEditFrom={
              isReview ? () => onModeChange?.(WORKSPACE_MODES.EDIT) : null
            }
          />
        )}

        {isReview && (
          <ScenarioReview
            hasResult={Boolean(result)}
            kpis={kpis}
            panels={panels}
            shownVersion={shownVersion}
            pricedAt={shownRun?.pricedAt || (result ? doc?.updatedAt : null)}
            pricedBy={shownRun?.pricedBy}
            inputCells={inputCells}
            onEdit={() => onModeChange?.(WORKSPACE_MODES.EDIT)}
          />
        )}

        {/* The form stays MOUNTED in Review (just hidden), so switching modes
            keeps unpriced edits and the field validation that blocks a price. */}
        <Box sx={{ display: isReview ? "none" : "block" }}>
          <Workbench>
            {/* Every Rff field shows "was …" when it differs from the version
                on screen (sizerUi WasMarker). */}
            <ChangedFieldsProvider value={changedMap}>
              <Box>{form}</Box>
            </ChangedFieldsProvider>
            <ResultsCol>
              <Flx column g={2}>
                <RailActions
                  hasScenario={hasScenario}
                  shownVersion={shownVersion}
                  nextVersion={nextVersion}
                  hasResult={Boolean(result)}
                  changes={changes}
                  onPrice={onPrice}
                  pending={pricePending}
                  error={priceError}
                  blockedReason={blockedReason}
                  liveNote={liveNote}
                  onExplain={onExplain}
                />
                <StaleWrap stale={dirty && Boolean(result)}>{rail}</StaleWrap>
                {railAfter}
                {hasScenario && result && (
                  <WsSecondaryButton
                    onClick={() => onModeChange?.(WORKSPACE_MODES.REVIEW)}
                    startIcon={<VisibilityOutlined />}
                    sx={{ width: "100%", justifyContent: "center" }}
                  >
                    See full results
                  </WsSecondaryButton>
                )}
              </Flx>
            </ResultsCol>
          </Workbench>
        </Box>

        {children}
      </SizerShell>
      {/* The floating "N changes since vN · Discard · Re-price → vN+1" pill —
          Inputs view only; the Results view has no form to re-price. */}
      {!isReview && (
        <RepricePill
          rootRef={rootRef}
          onExplain={onExplain}
          onHeight={setPillH}
          hasScenario={hasScenario}
          hasResult={Boolean(result)}
          shownVersion={shownVersion}
          nextVersion={nextVersion}
          changes={changes}
          onPrice={onPrice}
          onDiscard={onDiscard}
          pending={pricePending}
          error={priceError}
          blockedReason={blockedReason}
        />
      )}
    </Box>
  );
}
